import { FullSingleOrder } from "@/types";
import { arrayToCSV, downloadCSV } from "@/utils/Exports/export.util";
import { formatDatePretty, genericFailureToast } from "@/utils/utils.util";
import * as XLSX from "xlsx";

// #region exportSingleOrderToExcel
export function exportSingleOrderToExcel(singleOrder: FullSingleOrder) {
  try {
    // Using xlsx for broad compatibility with Excel and ease of use
    // Prepare order details as a single-row object for clarity in Excel
    const orderDetails = [
      {
        "Order ID": singleOrder.id,
        "Branch ID": singleOrder.branch_id,
        "Quote ID": singleOrder.quote_id,
        "User ID": singleOrder.created_by_user_id,
        "Quoted By": singleOrder.quoted_by,
        "File ID": singleOrder.file_id,
        "Quoted By Name": singleOrder.quoted_by_name,
        "DN Issued": singleOrder.delivery_note_issued,
        "DN POD": singleOrder.delivery_note_proof_of_delivery,
        "NC Number": singleOrder.nc_number,
        "File Drawing No": singleOrder.file_drawing_no,
      },
    ];

    // Flatten order items for tabular representation in Excel
    const orderItems = singleOrder.order_items.map((item) => ({
      "Item ID": item.id,
      "Order ID": item.order_id,
      "Part No": item.part_no,
      "Length (mm)": item.length,
      "Width (mm)": item.width,
      "Material Grade": item.material_grade,
      "Thickness (mm)": item.thickness,
      "Weight (kg)": item.weight,
      Quantity: item.qty,
      Cut: item.cut,
      Bend: item.bend,
      Shot: item.shot,
      "DN Issued": singleOrder.delivery_note_issued,
      "DN POD": singleOrder.delivery_note_proof_of_delivery,
      "Drawing No": item.item_file_drawing_no,
      "NC Number": singleOrder.nc_number,
      Query: singleOrder.assigned_to,
      "Created At": formatDatePretty(item.created_at, true, false),
    }));

    // Creating workbook and sheets for separation of concerns and user clarity
    const wb = XLSX.utils.book_new();
    const wsOrderDetails = XLSX.utils.json_to_sheet(orderDetails);
    const wsOrderItems = XLSX.utils.json_to_sheet(orderItems);

    // Set column widths to improve readability for longer names
    // Chosen widths are based on expected maximum content length for each column
    wsOrderDetails["!cols"] = [
      { wch: 15 }, // Order ID
      { wch: 12 }, // Branch ID
      { wch: 12 }, // Quote ID
      { wch: 12 }, // User ID
      { wch: 18 }, // Quoted By
      { wch: 12 }, // File ID
      { wch: 20 }, // Quoted By Name
      { wch: 9 }, // DN Issued
      { wch: 8 }, // DN POD
      { wch: 15 }, // NC Number
      { wch: 18 }, // File Drawing No
    ];
    wsOrderItems["!cols"] = [
      { wch: 12 }, // Item ID
      { wch: 12 }, // Order ID
      { wch: 22 }, // Part No
      { wch: 10 }, // Length
      { wch: 10 }, // Width
      { wch: 16 }, // Material Type
      { wch: 14 }, // Thickness
      { wch: 10 }, // Weight
      { wch: 8 }, // Quantity
      { wch: 6 }, // Cut
      { wch: 6 }, // Bend
      { wch: 6 }, // Shot
      { wch: 24 }, // Created At
    ];

    XLSX.utils.book_append_sheet(wb, wsOrderDetails, "Order Details");
    XLSX.utils.book_append_sheet(wb, wsOrderItems, "Order Items");

    // Use order id in filename for traceability
    XLSX.writeFile(wb, `Order_${singleOrder.id}.xlsx`);
  } catch (error) {
    console.error("Error exporting order to Excel:", error);
    genericFailureToast("Failed to export order to Excel. Please try again.");
  }
}
// #endregion exportSingleOrderToExcel

// #region exportSingleOrderDetailsToCSV
function exportSingleOrderDetailsToCSV(
  singleOrderDetails: Omit<FullSingleOrder, "order_items">
) {
  // Using a single-row CSV for order details to match Excel export and for clarity
  try {
    const details = [
      {
        "Order ID": singleOrderDetails.id,
        "Branch ID": singleOrderDetails.branch_id,
        "Quote ID": singleOrderDetails.quote_id,
        "User ID": singleOrderDetails.created_by_user_id,
        "Quoted By": singleOrderDetails.quoted_by,
        "File ID": singleOrderDetails.file_id,
        "Quoted By Name": singleOrderDetails.quoted_by_name,
        "DN Issued": singleOrderDetails.delivery_note_issued,
        "DN POD": singleOrderDetails.delivery_note_proof_of_delivery,
        "NC Number": singleOrderDetails.nc_number,
        "File Drawing No": singleOrderDetails.file_drawing_no,
      },
    ];
    // Using order id in filename for traceability
    downloadCSV(
      arrayToCSV(details),
      `Order_${singleOrderDetails.id}_Details.csv`
    );
  } catch (error) {
    console.error("Error exporting order details to CSV:", error);
    genericFailureToast(
      "Failed to export order details to CSV. Please try again."
    );
  }
}
// #endregion exportSingleOrderDetailsToCSV

// #region exportSingleOrderItemsToCSV
function exportSingleOrderItemsToCSV(singleOrder: FullSingleOrder) {
  // Exporting items as tabular CSV for compatibility with spreadsheet tools

  const singleOrderItems = singleOrder.order_items;
  try {
    const items = singleOrderItems.map((item) => ({
      "Item ID": item.id,
      "Order ID": item.order_id,
      "Part No": item.part_no,
      "Length (mm)": item.length,
      "Width (mm)": item.width,
      "Material Type": item.material_grade,
      "Thickness (mm)": item.thickness,
      "Weight (kg)": item.weight,
      Quantity: item.qty,
      Cut: item.cut,
      Bend: item.bend,
      Shot: item.shot,
      "DN Issued": singleOrder.delivery_note_issued,
      "DN POD": singleOrder.delivery_note_proof_of_delivery,
      "Drawing No": item.item_file_drawing_no,
      "NC Number": singleOrder.nc_number,
      Query: singleOrder.assigned_to,
      "Created At": formatDatePretty(item.created_at, true, false),
    }));
    // Using order id for filename for traceability
    downloadCSV(arrayToCSV(items), `Order_${singleOrder.id}_Items.csv`);
  } catch (error) {
    console.error("Error exporting order items to CSV:", error);
    genericFailureToast(
      "Failed to export order items to CSV. Please try again."
    );
  }
}
// #endregion exportSingleOrderItemsToCSV

// #region exportSingleOrderToCSVs
export function exportSingleOrderToCSVs(singleQuote: FullSingleOrder) {
  // Keeping this function as orchestrator for both CSV exports for clarity
  try {
    exportSingleOrderDetailsToCSV(singleQuote);
    exportSingleOrderItemsToCSV(singleQuote);
  } catch (error) {
    console.error("Error exporting order to CSV:", error);
    genericFailureToast("Failed to export order to CSV. Please try again.");
  }
}
// #endregion exportSingleOrderToCSVs
